import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  Input,
  viewChild,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { combineLatest } from 'rxjs';

import { debounceAsap, trackByProp } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  CheckboxComponent,
  DividerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type {
  DataSource,
  DataSourceLayout,
  DataSourceSwimlane,
  Grouping,
  ViewForm,
} from '@core/views-types';
import { ViewNameInputComponent } from '@core/views-ui';
import { DataSourceMoreSettingsCmp } from '@core/views-util';
import { VisibilitySelectorAnalytics, VisibilitySelectorService } from '@core/visibility-legacy';

import { ViewsAnalytics } from '../../analytics/views-analytics';
import { ViewLayoutPickerComponent } from '../view-layout-picker/view-layout-picker.component';
import { ViewSettingsFieldsComponent } from './view-settings-fields/view-settings-fields.component';
import { ViewSettingsGroupsComponent } from './view-settings-groups/view-settings-groups.component';
import { ViewSettingsMoreComponent } from './view-settings-more/view-settings-more.component';
import { ViewSettingsPinnedFiltersComponent } from './view-settings-pinned-filters/view-settings-pinned-filters.component';
import { ViewSettingsSwimlanesComponent } from './view-settings-swimlanes/view-settings-swimlanes.component';
import { ViewSettingsVisibilityComponent } from './view-settings-visibility/view-settings-visibility.component';

export type ViewSettingsOpenAt = 'groupingSelectTmpl' | 'mainTemplate';

@Component({
  selector: 'rt-views-view-settings',
  imports: [
    CheckboxComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconComponent,
    RtTooltip,
    TranslatePipe,
    ViewLayoutPickerComponent,
    ViewNameInputComponent,
    ViewSettingsFieldsComponent,
    ViewSettingsGroupsComponent,
    ViewSettingsMoreComponent,
    ViewSettingsSwimlanesComponent,
    ViewSettingsVisibilityComponent,
    ReactiveFormsModule,
    ViewSettingsPinnedFiltersComponent,
    DividerComponent,
  ],
  templateUrl: './view-settings.component.html',
  styleUrls: ['./view-settings.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [VisibilitySelectorService, VisibilitySelectorAnalytics],
})
export class ViewSettingsComponent implements OnInit {
  public readonly DropdownWidth = DropdownWidth;

  public readonly dataSource = input<DataSource>(undefined!);
  public readonly layouts = input<DataSourceLayout[]>(undefined!);
  public readonly swimlanes = input<DataSourceSwimlane[]>(undefined!);
  public readonly groups = input<Grouping[]>(undefined!);
  public readonly hasMoreSettings = input<boolean>(false);
  public isViewCreate = input.required<boolean>();
  public isViewPredefined = input.required<boolean>();
  public openAt = input<ViewSettingsOpenAt>('mainTemplate');
  @Input() public viewForm: ViewForm;

  public duplicate = output();
  public delete = output();

  public trackById = trackByProp<{ id: string | null }>({ prop: 'id' });

  public isDeleteDisabled = computed(() => this.isViewPredefined() || this.isViewCreate());

  public viewsAnalytics = inject(ViewsAnalytics);
  public dataSourceMoreSettingsCmp = inject(DataSourceMoreSettingsCmp, { optional: true });

  private mainDropdown = viewChild.required(DropdownComponent);
  private groupsComponent = viewChild.required(ViewSettingsGroupsComponent);

  private untilDestroyed = injectUntilDestroyed();
  private cdRef = inject(ChangeDetectorRef);

  private handleOpenAt = combineLatest([
    toObservable(this.openAt),
    toObservable(this.mainDropdown),
    toObservable(this.groupsComponent),
  ])
    .pipe(debounceAsap(), this.untilDestroyed)
    .subscribe(([openAt, mainDropdown, groupsComponent]) => {
      mainDropdown.clearTemplates();
      if (openAt === 'groupingSelectTmpl') {
        mainDropdown.setNestedTemplate(groupsComponent.groupingTmpl());
        mainDropdown.setNestedTemplate(groupsComponent.groupingSelectTmpl());
      }
    });

  public ngOnInit() {
    this.viewForm.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }

  public handleAutoFitClick() {
    const { autoFit } = this.viewForm.controls.layout.controls;
    autoFit.setValue(!autoFit.value);
    this.viewsAnalytics.autoFit({ isEnabled: autoFit.value });
  }
}
